import React, { memo, useRef } from 'react';
import { pick } from 'ramda';
import Jackpot from '../../../../../../../main/Games/GamesJackpot/GamesJackpot';

import style from './GameImageWrapper.scss';
import { useJackpotsConfiguration } from '../../../../../../../system/App/hooks/useJackpotsConfiguration/useJackpotsConfiguration';

import { Image } from 'js/components/common/Image';
import { CategoryName } from '../CategoryName';
import { ITranslateMethod } from 'js/hooks/i18n/types';
import { useDeviceSelector } from 'js/store/hooks';
import { clsx } from 'js/utils/classNames.util';
import { usePortraitImage } from '../../hooks/usePortraitImage';
import { useGameImageAnimation } from '../../hooks/useGameImageAnimation';
import { useGameImageFallback } from '@theme/js/hooks/swiss/useGameImageFallback';
import { useGetGreenTubeAndSkyWindEnablad } from '@theme/js/hooks/swiss/useGetGreenTubeAndSkyWindEnablad';

declare module 'react' {
    interface HTMLAttributes<T> extends AriaAttributes, DOMAttributes<T> {
        fetchpriority?: 'high' | 'low' | 'auto';
    }
}

type GameImageWrapperProps = {
    code: string;
    longImg: string;
    image: string;
    showGameDetails?: boolean;
    t: ITranslateMethod;
    categoryName?: string;
    isRecommended?: boolean;
    mostPlayedGame?: string;
    username?: string;
    isBecauseYouPlayed?: boolean;
    isCategoryName?: boolean;
    className?: string;
    name: string;
}

const GameImageWrapper = ({
    code,
    longImg,
    image,
    showGameDetails = false,
    t,
    categoryName,
    isRecommended,
    mostPlayedGame,
    username,
    isBecauseYouPlayed,
    isCategoryName,
    name
}: GameImageWrapperProps) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-len
    const { showJackpots, showGuaranteedHitTime } = useJackpotsConfiguration() as any;
    const { isPortrait } = useDeviceSelector(
        pick(['isPortrait'])
    );
    const { showGreenTubeJackpots, showSkyWindJackpots } = useGetGreenTubeAndSkyWindEnablad();

    const jackpotRef = useRef<HTMLDivElement | null>(null);

    const { shouldUsePortraitImage } = usePortraitImage(showGameDetails);

    const { animationName, renderGhostElement } = useGameImageAnimation(!showGameDetails);

    const {
        portraitImageHandleError,
        portraitImageSrc,
        landscapeImageHandleError,
        landscapeImageSrc
    } = useGameImageFallback({
        gameCode: code,
        longImg,
        image
    });

    return (
        <>
            {renderGhostElement({ style, name, longImg })}
            <div
                className={clsx([
                    style.gameImageWrapper,
                    showGameDetails ? `${style.gameImageWrapperShowDetails}` : ''
                ])}
            >
                {showGameDetails && isPortrait && <CategoryName
                    className={clsx([style.categoryName, showGameDetails ? style.categoryNameShowDetails : ''])}
                    t={t}
                    categoryName={categoryName}
                    isRecommended={isRecommended}
                    mostPlayedGame={mostPlayedGame}
                    username={username}
                    isBecauseYouPlayed={isBecauseYouPlayed}
                    isCategoryName={isCategoryName}
                />}
                <div
                    style={{
                        animation: animationName ? `${animationName} 0.6s forwards` : undefined
                    }}
                    className={clsx([
                        style.assetsHolder,
                        showGameDetails ? style.assetsHolderShowDetails : ''
                    ])}>
                        <Image
                            className={clsx([
                                style.imagePortrait,
                                shouldUsePortraitImage ? style.visible : style.invisible
                            ])}
                            alt={name}
                            src={portraitImageSrc}
                            fetchpriority="high"
                            onError={portraitImageHandleError}
                        />
                        <Image
                            className={clsx([
                                style.imageLandscape,
                                !shouldUsePortraitImage ? style.visible : style.invisible
                            ])}
                            alt={name}
                            src={landscapeImageSrc}
                            fetchpriority="high"
                            onError={landscapeImageHandleError}
                        />
                </div>
                <div
                    ref={jackpotRef}
                    className={clsx([
                        style.jackpotWrapper,
                        showGameDetails ? style.jackpotWrapperShowDetails : ''
                    ])}>
                    <Jackpot
                        targetElementSelector={'[class^="PopupManager"]'}
                        gameCode={code}
                        className={style.jackpot}
                        showGuaranteedHitTime={showGuaranteedHitTime}
                        isShowJackpot={showJackpots}
                        showGreenTube={showGreenTubeJackpots}
                        showSkyWind={showSkyWindJackpots}
                        isRegularJackpot={showJackpots}
                        isInGamePortlet={false}
                    />
                </div>
            </div>
        </>
    );
};

export default memo(GameImageWrapper);
